import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Modal, Form, Select, Input, DatePicker, Space, Popconfirm, message, Row, Col, Statistic } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, ExportOutlined, FilterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface Order {
  key: string;
  id: string;
  userName: string;
  userPhone: string;
  carModel: string;
  plateNumber: string;
  pickupDate: string;
  returnDate: string;
  pickupLocation: string;
  returnLocation: string;
  totalAmount: number;
  deposit: number;
  status: 'pending' | 'confirmed' | 'picked_up' | 'returned' | 'cancelled';
  paymentStatus: 'unpaid' | 'paid' | 'refunded';
  createdAt: string;
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);

  // シミュレート数据
  const mockOrders: Order[] = [
    {
      key: '1',
      id: '2024010101',
      userName: '张三',
      userPhone: '13800138001',
      carModel: '大众 Polo',
      plateNumber: '京A12345',
      pickupDate: '2024-02-01 09:00:00',
      returnDate: '2024-02-03 18:00:00',
      pickupLocation: '中关村店',
      returnLocation: '中关村店',
      totalAmount: 176,
      deposit: 1000,
      status: 'confirmed',
      paymentStatus: 'paid',
      createdAt: '2024-01-28 14:30:00',
    },
    {
      key: '2',
      id: '2024010102',
      userName: '李四',
      userPhone: '13800138002',
      carModel: '本田 Civic',
      plateNumber: '京B67890',
      pickupDate: '2024-01-25 10:00:00',
      returnDate: '2024-01-27 20:00:00',
      pickupLocation: '国贸店',
      returnLocation: '机场店',
      totalAmount: 236,
      deposit: 1500,
      status: 'returned',
      paymentStatus: 'paid',
      createdAt: '2024-01-20 16:45:00',
    },
    {
      key: '3',
      id: '2024010103',
      userName: '王五',
      userPhone: '13800138003',
      carModel: '丰田 Camry',
      plateNumber: '京C11111',
      pickupDate: '2024-01-15 14:00:00',
      returnDate: '2024-01-16 12:00:00',
      pickupLocation: '火车站店',
      returnLocation: '火车站店',
      totalAmount: 168,
      deposit: 2000,
      status: 'cancelled',
      paymentStatus: 'refunded',
      createdAt: '2024-01-10 11:20:00',
    },
    {
      key: '4',
      id: '2024010104',
      userName: '赵六',
      userPhone: '13800138004',
      carModel: '奔驰 C200',
      plateNumber: '京D22222',
      pickupDate: '2024-02-05 15:00:00',
      returnDate: '2024-02-06 15:00:00',
      pickupLocation: '机场店',
      returnLocation: '机场店',
      totalAmount: 288,
      deposit: 3000,
      status: 'pending',
      paymentStatus: 'unpaid',
      createdAt: '2024-02-01 09:15:00',
    },
  ];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = () => {
    setLoading(true);
    // シミュレートAPI调用
    setTimeout(() => {
      setOrders(mockOrders);
      setFilteredOrders(mockOrders);
      setLoading(false);
    }, 500);
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'orange',
      confirmed: 'blue',
      picked_up: 'green',
      returned: 'purple',
      cancelled: 'red',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      pending: '待確認',
      confirmed: '已確認',
      picked_up: '使用中',
      returned: '已归还',
      cancelled: '已キャンセル',
    };
    return textMap[status] || status;
  };

  const getPaymentStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      unpaid: 'red',
      paid: 'green',
      refunded: 'orange',
    };
    return colorMap[status] || 'default';
  };

  const getPaymentStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      unpaid: '未支付',
      paid: '已支付',
      refunded: '已退款',
    };
    return textMap[status] || status;
  };

  const handleSearch = (values: any) => {
    let filtered = orders;
    
    if (values.orderId) {
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(values.orderId.toLowerCase())
      );
    }
    
    if (values.userName) {
      filtered = filtered.filter(order => 
        order.userName.includes(values.userName)
      );
    }
    
    if (values.status) {
      filtered = filtered.filter(order => order.status === values.status);
    }
    
    if (values.dateRange && values.dateRange.length === 2) {
      const [startDate, endDate] = values.dateRange;
      filtered = filtered.filter(order => {
        const orderDate = dayjs(order.createdAt);
        return orderDate.isAfter(startDate) && orderDate.isBefore(endDate);
      });
    }
    
    setFilteredOrders(filtered);
    message.success(`找到 ${filtered.length} 条注文记录`);
  };

  const handleEdit = (record: Order) => {
    setSelectedOrder(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleViewDetail = (record: Order) => {
    setSelectedOrder(record);
    setDetailModalVisible(true);
  };

  const handleDelete = (orderId: string) => {
    const newOrders = orders.filter(order => order.id !== orderId);
    setOrders(newOrders);
    setFilteredOrders(newOrders);
    message.success('注文削除成功');
  };

  const handleUpdateOrder = (values: any) => {
    if (!selectedOrder) return;
    
    const updatedOrders = orders.map(order => 
      order.id === selectedOrder.id ? { ...order, ...values } : order
    );
    
    setOrders(updatedOrders);
    setFilteredOrders(updatedOrders);
    setEditModalVisible(false);
    message.success('注文更新成功');
  };

  const handleExport = () => {
    message.success('注文数据导出成功');
  };

  const calculateStatistics = () => {
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.totalAmount, 0);
    const pendingOrders = filteredOrders.filter(order => order.status === 'pending').length;
    const completedOrders = filteredOrders.filter(order => order.status === 'returned').length;
    
    return {
      totalOrders,
      totalRevenue,
      pendingOrders,
      completedOrders,
    };
  };

  const statistics = calculateStatistics();

  const columns: ColumnsType<Order> = [
    {
      title: '注文号',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'ユーザー情報',
      key: 'userInfo',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.userName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.userPhone}</div>
        </div>
      ),
    },
    {
      title: '車両情報',
      key: 'carInfo',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.carModel}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.plateNumber}</div>
        </div>
      ),
    },
    {
      title: '租车時間',
      key: 'rentalTime',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>
            取: {dayjs(record.pickupDate).format('MM-DD HH:mm')}
          </div>
          <div style={{ fontSize: 12 }}>
            还: {dayjs(record.returnDate).format('MM-DD HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: '地点',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>取: {record.pickupLocation}</div>
          <div style={{ fontSize: 12 }}>还: {record.returnLocation}</div>
        </div>
      ),
    },
    {
      title: '金额',
      key: 'amount',
      width: 100,
      render: (_, record) => (
        <div>
          <div>¥{record.totalAmount}</div>
          <div style={{ fontSize: 12, color: '#666' }}>押金: ¥{record.deposit}</div>
        </div>
      ),
    },
    {
      title: 'ステータス',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <div>
          <Tag color={getStatusColor(record.status)}>
            {getStatusText(record.status)}
          </Tag>
          <br />
          <Tag color={getPaymentStatusColor(record.paymentStatus)} style={{ marginTop: 4 }}>
            {getPaymentStatusText(record.paymentStatus)}
          </Tag>
        </div>
      ),
    },
    {
      title: '作成時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (text) => dayjs(text).format('MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            詳細
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Popconfirm
            title="确定要削除这个注文吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="キャンセル"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>注文管理</h1>
      
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总注文数" value={statistics.totalOrders} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="总收入" value={statistics.totalRevenue} prefix="¥" />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="待处理注文" value={statistics.pendingOrders} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已完成注文" value={statistics.completedOrders} />
          </Card>
        </Col>
      </Row>

      {/* 検索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={searchForm}
          onFinish={handleSearch}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="orderId">
            <Input placeholder="注文号" prefix={<SearchOutlined />} />
          </Form.Item>
          <Form.Item name="userName">
            <Input placeholder="ユーザー氏名" />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="注文ステータス" style={{ width: 120 }}>
              <Option value="">全部</Option>
              <Option value="pending">待確認</Option>
              <Option value="confirmed">已確認</Option>
              <Option value="picked_up">使用中</Option>
              <Option value="returned">已归还</Option>
              <Option value="cancelled">已キャンセル</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始時間', '结束時間']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                検索
              </Button>
              <Button onClick={() => {
                searchForm.resetFields();
                setFilteredOrders(orders);
              }}>
                重置
              </Button>
              <Button icon={<ExportOutlined />} onClick={handleExport}>
                导出
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 注文表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredOrders}
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            total: filteredOrders.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>

      {/* 編集注文模态框 */}
      <Modal
        title="編集注文"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        footer={null}
      >
        <Form
          form={form}
          onFinish={handleUpdateOrder}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="注文ステータス"
                rules={[{ required: true, message: 'お选择注文ステータス' }]}
              >
                <Select>
                  <Option value="pending">待確認</Option>
                  <Option value="confirmed">已確認</Option>
                  <Option value="picked_up">使用中</Option>
                  <Option value="returned">已归还</Option>
                  <Option value="cancelled">已キャンセル</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentStatus"
                label="支付ステータス"
                rules={[{ required: true, message: 'お选择支付ステータス' }]}
              >
                <Select>
                  <Option value="unpaid">未支付</Option>
                  <Option value="paid">已支付</Option>
                  <Option value="refunded">已退款</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="totalAmount" label="注文金额">
                <Input prefix="¥" type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deposit" label="押金">
                <Input prefix="¥" type="number" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                キャンセル
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 注文詳細模态框 */}
      <Modal
        title="注文詳細"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={700}
        footer={null}
      >
        {selectedOrder && (
          <div>
            <h4>基本情報</h4>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>注文号：</strong>{selectedOrder.id}
              </Col>
              <Col span={8}>
                <strong>ユーザー：</strong>{selectedOrder.userName}
              </Col>
              <Col span={8}>
                <strong>携帯電話：</strong>{selectedOrder.userPhone}
              </Col>
            </Row>
            
            <h4>車両情報</h4>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>车型：</strong>{selectedOrder.carModel}
              </Col>
              <Col span={12}>
                <strong>车牌号：</strong>{selectedOrder.plateNumber}
              </Col>
            </Row>
            
            <h4>租车詳細</h4>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>取车時間：</strong>{selectedOrder.pickupDate}
              </Col>
              <Col span={12}>
                <strong>还车時間：</strong>{selectedOrder.returnDate}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>取车地点：</strong>{selectedOrder.pickupLocation}
              </Col>
              <Col span={12}>
                <strong>还车地点：</strong>{selectedOrder.returnLocation}
              </Col>
            </Row>
            
            <h4>费用情報</h4>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>注文金额：</strong>¥{selectedOrder.totalAmount}
              </Col>
              <Col span={8}>
                <strong>押金：</strong>¥{selectedOrder.deposit}
              </Col>
              <Col span={8}>
                <strong>作成時間：</strong>{selectedOrder.createdAt}
              </Col>
            </Row>
            
            <h4>ステータス情報</h4>
            <Row gutter={16}>
              <Col span={12}>
                <strong>注文ステータス：</strong>
                <Tag color={getStatusColor(selectedOrder.status)} style={{ marginLeft: 8 }}>
                  {getStatusText(selectedOrder.status)}
                </Tag>
              </Col>
              <Col span={12}>
                <strong>支付ステータス：</strong>
                <Tag color={getPaymentStatusColor(selectedOrder.paymentStatus)} style={{ marginLeft: 8 }}>
                  {getPaymentStatusText(selectedOrder.paymentStatus)}
                </Tag>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderManagement;